import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, ButtonBase, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItemAvatar, ListItemButton, ListItemText, Snackbar, TextField, Typography,
} from '@mui/material';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useBiometric } from '../hooks/useBiometric';
import { AuthDialog } from './AuthDialog';
import { Gateway, NodeSettings, NodeType } from '../types';
import { NODE_TYPES, getNodeTypeConfig } from './nodeTypes';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { statesUrl, settingsUrl, toggleUrl, commandUrl, parseStates } from '../boardApi';
import { getNodeCardComponent, NodeCardProps } from './nodeCards';
import { GenericNodeCard } from './nodeCards/GenericNodeCard';

interface Props {
  gateway: Gateway;
  onChange: (gateway: Gateway) => void;
  refreshKey?: number;
  hideFirstNode?: boolean;
}

const PULL_THRESHOLD = 64;

export const NodeList: React.FC<Props> = ({ gateway, onChange, refreshKey, hideFirstNode }) => {
  const { t } = useLanguage();
  const { getAuthHeader, activeHouseId } = useAuth();
  const biometric = useBiometric(activeHouseId ?? '');
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [pendingToggleIndex, setPendingToggleIndex] = useState<number | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editType, setEditType] = useState<NodeType>('generic');
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const touchStartYRef = useRef<number | null>(null);
  const pullYRef = useRef(0);
  const [pullYDisplay, setPullYDisplay] = useState(0);
  const mountedRef = useRef(false);

  const openEdit = (index: number) => {
    setEditIndex(index);
    setEditName(gateway.nodes[index].label || t.nodeDefaultLabel(index));
    setEditDesc(gateway.nodes[index].description || t.nodeDefaultDescription(index));
    setEditType(gateway.nodes[index].type ?? 'generic');
  };

  const saveEdit = () => {
    if (editIndex === null) return;
    const newNodes = [...gateway.nodes];
    newNodes[editIndex] = { ...newNodes[editIndex], label: editName, description: editDesc, type: editType };
    onChange({ ...gateway, nodes: newNodes });
    setEditIndex(null);
  };

  useEffect(() => {
    if (!gateway.url) return;

    if (!mountedRef.current) {
      mountedRef.current = true;
      if (gateway.lastFetchedAt && Date.now() - gateway.lastFetchedAt < 30_000) return;
    }

    let cancelled = false;

    const refresh = async () => {
      onChange({
        ...gateway,
        nodes: gateway.nodes.map(n => ({ ...n, isLoading: true, error: undefined })),
      });

      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(statesUrl(gateway.url), { headers: getAuthHeader(), signal: controller.signal });
        clearTimeout(timer);
        if (cancelled) return;
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const states = parseStates(html);

        // Mijia-only: real per-node settings (fan speed, oscillation, ...) that
        // /relays.cgi's shared on/off-only shape can't carry -- best-effort, a
        // failure here must not blank out the state fetch above.
        let extraSettings: NodeSettings[] = [];
        if (gateway.kind === 'mijia') {
          try {
            const settingsController = new AbortController();
            const settingsTimer = setTimeout(() => settingsController.abort(), 8000);
            const settingsResponse = await fetch(
              settingsUrl(gateway.url), { headers: getAuthHeader(), signal: settingsController.signal });
            clearTimeout(settingsTimer);
            if (settingsResponse.ok) extraSettings = await settingsResponse.json();
          } catch {
            // state (on/off) already fetched above; settings just won't refresh this round
          }
        }
        if (cancelled) return;

        onChange({
          ...gateway,
          lastFetchedAt: Date.now(),
          nodes: gateway.nodes.map((n, i) => ({
            ...n,
            isLoading: false,
            state: i < states.length ? states[i] : null,
            settings: extraSettings[i] ? { ...n.settings, ...extraSettings[i] } : n.settings,
            error: undefined,
          })),
        });
      } catch {
        if (cancelled) return;
        onChange({
          ...gateway,
          nodes: gateway.nodes.map(n => ({ ...n, isLoading: false, error: t.errorConnection })),
        });
      }
    };

    refresh();
    return () => { cancelled = true; };
  }, [gateway.id, gateway.url, refreshKey, internalRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useAutoRefresh(() => setInternalRefreshKey(k => k + 1), 300_000);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const getScrollParentTop = (): number => {
      let p = el.parentElement;
      while (p) {
        if (p.scrollHeight > p.clientHeight) return p.scrollTop;
        p = p.parentElement;
      }
      return 0;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (getScrollParentTop() === 0) touchStartYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (touchStartYRef.current === null) return;
      const dy = e.touches[0].clientY - touchStartYRef.current;
      if (dy > 0) {
        pullYRef.current = Math.min(dy, PULL_THRESHOLD * 1.5);
        setPullYDisplay(pullYRef.current);
      } else {
        touchStartYRef.current = null;
        pullYRef.current = 0;
        setPullYDisplay(0);
      }
    };

    const onTouchEnd = () => {
      if (pullYRef.current >= PULL_THRESHOLD) setInternalRefreshKey(k => k + 1);
      touchStartYRef.current = null;
      pullYRef.current = 0;
      setPullYDisplay(0);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doToggle = async (index: number) => {
    const node = gateway.nodes[index];
    if (node.isLoading) return;

    const newNodes = [...gateway.nodes];

    newNodes[index] = { ...node, isLoading: true, error: undefined };
    onChange({ ...gateway, nodes: newNodes });

    if (!gateway.url) {
      newNodes[index] = { ...node, isLoading: false, error: t.errorNoUrl };
      onChange({ ...gateway, nodes: [...newNodes] });
      setToggleError(t.errorNoUrl);
      return;
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(toggleUrl(gateway.url, index), {
        headers: getAuthHeader(),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (response.ok) {
        // The toggle endpoint returns the same real states array /relays.cgi's
        // poll does (switchbot-adapter.py/miio-adapter.py's toggle() both end by
        // returning get_relay_states()) -- read it back instead of guessing the
        // inverse locally, which could drift from the server's own tracked state.
        const html = await response.text();
        const states = parseStates(html);
        const realState = index < states.length ? states[index] : node.state !== true;

        // Mijia only: flipping power doesn't confirm fan speed/oscillation are
        // still what we last knew (e.g. changed another way since the last poll)
        // -- refresh the whole card here too, not just the field just toggled.
        let freshSettings: NodeSettings | undefined;
        if (gateway.kind === 'mijia') {
          try {
            const settingsResponse = await fetch(settingsUrl(gateway.url), { headers: getAuthHeader() });
            if (settingsResponse.ok) {
              const allSettings: NodeSettings[] = await settingsResponse.json();
              freshSettings = allSettings[index];
            }
          } catch {
            // best-effort; the real power state above is accurate regardless
          }
        }

        newNodes[index] = {
          ...node, state: realState, isLoading: false, error: undefined,
          settings: freshSettings ? { ...node.settings, ...freshSettings } : node.settings,
        };
      } else {
        const errMsg = t.errorHttp(response.status);
        newNodes[index] = { ...node, state: node.state, isLoading: false, error: errMsg };
        setToggleError(errMsg);
      }
    } catch {
      const errMsg = t.errorConnection;
      newNodes[index] = { ...node, isLoading: false, error: errMsg };
      setToggleError(errMsg);
    }

    onChange({ ...gateway, nodes: [...newNodes] });
  };

  const handleToggle = async (index: number) => {
    const node = gateway.nodes[index];
    if (node.type === 'door_lock') {
      setPendingToggleIndex(index);
      if (biometric.isEnrolled) {
        const ok = await biometric.authenticate();
        if (ok) { setPendingToggleIndex(null); await doToggle(index); return; }
      }
      setAuthDialogOpen(true);
      return;
    }
    await doToggle(index);
  };

  const handleSettingsChange = (index: number, settings: NodeSettings) => {
    const newNodes = [...gateway.nodes];
    newNodes[index] = { ...newNodes[index], settings };
    onChange({ ...gateway, nodes: newNodes });
  };

  const handleCommand = async (
    index: number, cmd: string, params?: Record<string, string>, optimisticSettings?: NodeSettings,
  ) => {
    const node = gateway.nodes[index];
    if (node.isLoading) return;

    if (!gateway.url) {
      setToggleError(t.errorNoUrl);
      return;
    }

    // Applied together with isLoading:true in ONE update, not via a separate
    // onSettingsChange call beforehand -- a second call here would read the same
    // pre-update `gateway` this closure already captured and silently discard
    // whatever a sibling onSettingsChange call just set (see NodeCardProps.onCommand).
    const baseSettings = optimisticSettings ? { ...node.settings, ...optimisticSettings } : node.settings;
    const newNodes = [...gateway.nodes];
    newNodes[index] = { ...node, isLoading: true, error: undefined, settings: baseSettings };
    onChange({ ...gateway, nodes: newNodes });

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(commandUrl(gateway.url, index, cmd, params), {
        headers: getAuthHeader(),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (response.ok) {
        // Xiaomi/Mijia's /api/command confirms the write against the real device
        // and echoes back what actually took effect ({state?, settings?});
        // SwitchBot IR has no such read-back and always answers with `{}`, so this
        // just keeps the optimistic settings applied above for IR nodes.
        const result = await response.json().catch(() => ({}));
        newNodes[index] = {
          ...node,
          isLoading: false,
          error: undefined,
          ...(result.state !== undefined ? { state: result.state } : {}),
          settings: result.settings ? { ...baseSettings, ...result.settings } : baseSettings,
        };
      } else {
        const errMsg = response.status === 404 ? t.cmdNotSupported : t.errorHttp(response.status);
        newNodes[index] = { ...node, isLoading: false, error: errMsg };
        setToggleError(errMsg);
      }
    } catch {
      newNodes[index] = { ...node, isLoading: false, error: t.errorConnection };
      setToggleError(t.errorConnection);
    }

    onChange({ ...gateway, nodes: [...newNodes] });
  };

  return (
    <Box ref={rootRef} sx={{ p: 2 }}>
      {/* Auth dialog for door_lock nodes */}
      <AuthDialog
        open={authDialogOpen}
        onSuccess={() => {
          setAuthDialogOpen(false);
          if (pendingToggleIndex !== null) doToggle(pendingToggleIndex);
          setPendingToggleIndex(null);
        }}
        onClose={() => { setAuthDialogOpen(false); setPendingToggleIndex(null); }}
      />
      {/* Pull-to-refresh indicator */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        height: pullYDisplay > 0 ? Math.min(pullYDisplay / 2, 40) : 0,
        transition: pullYDisplay === 0 ? 'height 0.2s ease' : 'none',
      }}>
        <CircularProgress size={24} sx={{ opacity: Math.min(pullYDisplay / PULL_THRESHOLD, 1) }} />
      </Box>

      {gateway.nodes.map((node, index) => {
        if (hideFirstNode && index === 0) return null;
        const NodeCard: React.FC<NodeCardProps> = (gateway.kind === 'switchbot' || gateway.kind === 'mijia')
          ? getNodeCardComponent(node.type)
          : GenericNodeCard;
        return (
          <NodeCard
            key={index}
            node={node}
            index={index}
            gateway={gateway}
            onToggle={() => handleToggle(index)}
            onCommand={(cmd, params) => handleCommand(index, cmd, params)}
            onSettingsChange={(settings) => handleSettingsChange(index, settings)}
            onEditOpen={() => openEdit(index)}
          />
        );
      })}

      <Dialog open={editIndex !== null} onClose={() => setEditIndex(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t.nodeEditTitle}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth autoFocus label={t.nodeEditName} value={editName}
            onChange={e => setEditName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label={t.nodeEditDescription} value={editDesc}
            onChange={e => setEditDesc(e.target.value)}
            sx={{ mb: 2 }}
          />
          <ButtonBase
            onClick={() => setTypePickerOpen(true)}
            aria-label={t.nodeEditType}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, width: '100%',
              justifyContent: 'flex-start', textAlign: 'left',
              cursor: 'pointer', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider',
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'background.default' }}>
              {(() => { const { Icon } = getNodeTypeConfig(editType); return <Icon sx={{ fontSize: 20, color: 'text.secondary' }} />; })()}
            </Avatar>
            <Box flex={1}>
              <Typography variant="caption" color="text.secondary">{t.nodeEditType}</Typography>
              <Typography variant="body2">{t[getNodeTypeConfig(editType).labelKey] as string}</Typography>
            </Box>
          </ButtonBase>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditIndex(null)}>{t.nodeEditCancel}</Button>
          <Button onClick={saveEdit} variant="contained">{t.nodeEditSave}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={typePickerOpen} onClose={() => setTypePickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t.nodeEditType}</DialogTitle>
        <List disablePadding>
          {NODE_TYPES.map(cfg => (
            <ListItemButton
              key={cfg.type}
              onClick={() => { setEditType(cfg.type); setTypePickerOpen(false); }}
              selected={editType === cfg.type}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'background.default' }}>
                  <cfg.Icon sx={{ color: 'text.primary' }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={t[cfg.labelKey] as string} />
            </ListItemButton>
          ))}
        </List>
      </Dialog>

      <Snackbar
        open={toggleError !== null}
        autoHideDuration={3000}
        onClose={() => setToggleError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToggleError(null)} sx={{ width: '100%' }}>
          {toggleError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
